/**
 * Wishlist Functionality – Broadcast Theme (Immunosciences)
 * – localStorage-based, no backend required
 * – Wishlist PAGE renders cards via Shopify Section Rendering API
 *   using the theme's own api-product-grid-item section, so every
 *   card on the wishlist page looks exactly like a collection card.
 * – Event delegation + MutationObserver keep heart states in sync
 *   across collection, search, and AJAX-filtered pages.
 */

const WISHLIST_KEY = 'shopify_dawn_wishlist';

/* ─────────────────────────────────────────────
   Data Layer
───────────────────────────────────────────── */
const Wishlist = {
  getItems() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch { return []; }
  },

  saveItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  },

  has(productId) {
    return this.getItems().some((i) => String(i.id) === String(productId));
  },

  add(product) {
    const items = this.getItems();
    if (!this.has(product.id)) { items.push(product); this.saveItems(items); }
  },

  remove(productId) {
    this.saveItems(this.getItems().filter((i) => String(i.id) !== String(productId)));
  },

  toggle(product) {
    if (this.has(product.id)) { this.remove(product.id); return false; }
    this.add(product); return true;
  },

  count() { return this.getItems().length; },
  clear() { localStorage.removeItem(WISHLIST_KEY); },
};

/* ─────────────────────────────────────────────
   UI Helpers
───────────────────────────────────────────── */
function setButtonState(btn, isActive) {
  btn.classList.toggle('wishlist-btn--active', isActive);
  btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
  btn.setAttribute('aria-pressed', String(isActive));
  const icon = btn.querySelector('.wishlist-icon');
  if (icon) icon.setAttribute('fill', isActive ? 'currentColor' : 'none');
}

function updateWishlistCounter() {
  const count = Wishlist.count();
  document.querySelectorAll('.wishlist-count-bubble').forEach((el) => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function syncAllButtonStates() {
  const ids = new Set(Wishlist.getItems().map((i) => String(i.id)));
  document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
    const id = btn.dataset.productId;
    if (id) setButtonState(btn, ids.has(String(id)));
  });
}

/* ─────────────────────────────────────────────
   Event Delegation
   One listener on document catches all wishlist
   buttons — including AJAX-injected ones.
───────────────────────────────────────────── */
function initWishlistDelegation() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const product = {
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
      available: btn.dataset.productAvailable !== 'false',
      onSale: btn.dataset.productOnSale === 'true',
      compareAtPrice: btn.dataset.productCompareAtPrice || '',
    };

    if (!product.id) return;

    const added = Wishlist.toggle(product);
    setButtonState(btn, added);
    updateWishlistCounter();
    showToast(
      added
        ? `"${product.title}" added to wishlist`
        : `"${product.title}" removed from wishlist`
    );

    /* If we're on the wishlist page, re-render after removing */
    if (!added && document.getElementById('wishlist-page-container')) {
      requestAnimationFrame(() => {
        renderWishlistPage();
        updateWishlistCounter();
      });
    }
  });
}

/* ─────────────────────────────────────────────
   MutationObserver
   Re-syncs heart states after AJAX filter/sort.
───────────────────────────────────────────── */
function observeProductGrid() {
  const target = document.querySelector('main') || document.body;
  let debounce = null;

  new MutationObserver((mutations) => {
    if (!mutations.some((m) => m.addedNodes.length > 0)) return;
    clearTimeout(debounce);
    debounce = setTimeout(syncAllButtonStates, 200);
  }).observe(target, { childList: true, subtree: true });
}

/* ─────────────────────────────────────────────
   Toast
───────────────────────────────────────────── */
let toastTimer = null;

function showToast(message) {
  let toast = document.getElementById('wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('wishlist-toast--visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('wishlist-toast--visible'), 3000);
}

/* ─────────────────────────────────────────────
   Section Rendering API — fetch one product card
   Uses api-product-grid-item section which wraps
   product-grid-item — same card as collection pages.
───────────────────────────────────────────── */
async function fetchProductCardHTML(productUrl) {
  /* Extract handle from stored URL: /products/my-handle?variant=... → my-handle */
  const match = (productUrl || '').match(/\/products\/([^/?#]+)/);
  if (!match) return null;
  const handle = match[1];

  try {
    const res = await fetch(
      `/products/${handle}?sections=api-product-grid-item`,
      { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
    );
    if (!res.ok) return null;

    const data = await res.json();
    const sectionHTML = data['api-product-grid-item'];
    if (!sectionHTML) return null;

    /* api-product-grid-item wraps its output in [data-api-content] */
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');
    const content = doc.querySelector('[data-api-content]');
    return content ? content.innerHTML.trim() : null;
  } catch (err) {
    console.warn('[Wishlist] Could not fetch card for', handle, err);
    return null;
  }
}

/* ─────────────────────────────────────────────
   Wishlist Page Renderer
───────────────────────────────────────────── */
async function renderWishlistPage() {
  const container = document.getElementById('wishlist-page-container');
  if (!container) return;

  const items = Wishlist.getItems();
  const emptyState = document.getElementById('wishlist-empty-state');
  const grid = document.getElementById('wishlist-grid');
  const countEl = document.getElementById('wishlist-count-text');

  /* Update item count label */
  if (countEl) {
    const n = items.length;
    countEl.textContent = n === 0 ? '' : (n === 1 ? '1 item' : `${n} items`);
  }

  if (!items.length) {
    if (emptyState) emptyState.style.display = 'flex';
    if (grid) grid.innerHTML = '';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';
  if (!grid) return;

  /* Show skeleton placeholders while fetching */
  grid.innerHTML = items
    .map(() => '<div class="wishlist-skeleton"></div>')
    .join('');

  /* Fetch all cards in parallel */
  const cardHTMLs = await Promise.all(items.map((item) => fetchProductCardHTML(item.url)));

  /* Build grid — skip any failed fetches */
  const validCards = cardHTMLs.filter(Boolean);

  if (!validCards.length) {
    /* All fetches failed — fallback to empty state */
    if (emptyState) emptyState.style.display = 'flex';
    grid.innerHTML = '';
    return;
  }

  grid.innerHTML = validCards.join('');

  /* Sync heart states: already-wishlisted items should show filled hearts */
  syncAllButtonStates();
}

/* ─────────────────────────────────────────────
   Utilities
───────────────────────────────────────────── */
const _escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(str) {
  return (str || '').replace(/[&<>"']/g, (c) => _escapeMap[c]);
}

function escapeAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* ─────────────────────────────────────────────
   Backward-compat alias
───────────────────────────────────────────── */
function initWishlistButtons() { syncAllButtonStates(); }

/* ─────────────────────────────────────────────
   Bootstrap
───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initWishlistDelegation();  // global delegated click handler
  syncAllButtonStates();     // fill hearts for already-wishlisted items
  updateWishlistCounter();   // header badge
  observeProductGrid();      // re-sync after AJAX filter / sort / pagination
  renderWishlistPage();      // render native product cards on /pages/wishlist
});

document.addEventListener('shopify:section:load', () => {
  syncAllButtonStates();
  updateWishlistCounter();
  renderWishlistPage();
});